import { useEffect } from 'react';

/**
 * Brand suffix appended to every document title set via {@link useDocumentTitle}.
 * Keep in sync with the static `<title>` in `portal/index.html` so the initial
 * title (before React mounts) matches what we render afterwards.
 */
export const DOCUMENT_TITLE_BRAND = 'Logic Apps Automation';

/** Separator between segments and between segments and the brand suffix. */
export const DOCUMENT_TITLE_SEPARATOR = ' | ';

/**
 * Build a document title from page-supplied context segments.
 *
 * Filters out falsy/empty segments, joins the rest with " | ", and appends
 * the brand suffix. With no truthy segments the title is just the brand,
 * which matches the initial `<title>` in index.html.
 *
 * Exported for unit testing and for callers that need to compute the
 * string without mutating `document.title`.
 */
export function buildDocumentTitle(
  segments: ReadonlyArray<string | null | undefined>,
): string {
  const cleaned = segments
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s): s is string => s.length > 0);

  if (cleaned.length === 0) {
    return DOCUMENT_TITLE_BRAND;
  }

  return [...cleaned, DOCUMENT_TITLE_BRAND].join(DOCUMENT_TITLE_SEPARATOR);
}

/**
 * Sets `document.title` from the given context segments and the brand suffix.
 *
 * Pages should call this once at the top with the most-specific context first
 * (e.g. `[workflowName, appName]`). When pages are nested, the most-specific
 * (deepest) page's effect runs last and wins. There is no restore-on-unmount
 * to avoid races with React StrictMode double-effects and Suspense.
 *
 * The dependency array uses the joined string so callers can pass freshly
 * derived arrays each render without triggering unnecessary DOM writes.
 */
export function useDocumentTitle(
  segments: ReadonlyArray<string | null | undefined>,
): void {
  const next = buildDocumentTitle(segments);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.title !== next) {
      document.title = next;
    }
  }, [next]);
}
